import { type ReactNode, useLayoutEffect, useRef } from 'react'
import { ReconnectionBanner } from '@components/molecules/Alerts/ReconnectionBanner'
import { PageHeader } from '@components/layouts/PageHeader'
import clsx from 'clsx'

type PageLayoutRootProps = {
  children: ReactNode
  id?: string
}

function PageLayoutRoot({ children, id, ...props }: PageLayoutRootProps) {
  return (
    <div
      {...props}
      id={id}
      className={clsx(
        'tw:relative tw:flex tw:flex-col',
        'tw:w-screen tw:min-w-full tw:grow tw:overflow-x-clip',
        'tw:lg:w-sidebar-adjusted'
      )}
    >
      <ReconnectionBanner />
      {children}
    </div>
  )
}

type PageLayoutBodyProps = {
  children: ReactNode
  className?: string
}

function PageLayoutBody({ children }: PageLayoutBodyProps) {
  return <section className="tw:px-6 tw:py-4 tw:grow">{children}</section>
}

type PageLayoutFooterProps = {
  children: ReactNode
}

function PageLayoutFooter({ children }: PageLayoutFooterProps) {
  const ref = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const setHeight = () => document.documentElement.style.setProperty('--sb-footer-height', `${node.offsetHeight}px`)
    const observer = new ResizeObserver(setHeight)
    observer.observe(node)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--sb-footer-height')
    }
  }, [])

  return (
    <footer
      ref={ref}
      role="sectionfooter"
      className={clsx(
        'tw:sticky tw:right-0 tw:bottom-0 tw:z-40',
        'tw:w-full tw:px-6 tw:py-4',
        'tw:border tw:border-primary-100 tw:border-t tw:bg-white'
      )}
    >
      {children}
    </footer>
  )
}

export const PageLayout = {
  Root: PageLayoutRoot,
  Header: PageHeader,
  Body: PageLayoutBody,
  Footer: PageLayoutFooter,
}
